import { useState, useMemo, useCallback } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showSuccessToast } from "@/hooks/use-toast";
import { FolderTree } from "./components/FolderTree";
import { FilterBar, defaultFilters } from "./components/FilterBar";
import type { Filters } from "./components/FilterBar";
import { FileTable } from "./components/FileTable";
import type { SortField, SortOrder } from "./components/FileTable";
import { DocumentPreviewPanel } from "./components/DocumentPreviewPanel";
import { UploadDialog } from "./components/UploadDialog";
import { mockClients, mockDocuments, folderTree } from "./mockData";
import type { MockDocument } from "./types";

export function FileExplorerPage() {
  const [documents, setDocuments] = useState<MockDocument[]>(mockDocuments);
  const [selectedClientId, setSelectedClientId] = useState<string>("all");
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sortField, setSortField] = useState<SortField>("documentDate");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState(1);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [mirrorDocId, setMirrorDocId] = useState<string | null>(null);
  const [mirrorTargetClient, setMirrorTargetClient] = useState("");

  const pageSize = 10;

  // Filter + sort documents
  const filteredDocuments = useMemo(() => {
    let docs = [...documents];

    // Client filter
    if (selectedClientId !== "all") {
      docs = docs.filter((d) => d.clientId === selectedClientId);
    }

    // Folder filter
    if (selectedFolder) {
      docs = docs.filter(
        (d) => d.folder === selectedFolder || d.folder.startsWith(selectedFolder + "/")
      );
    }

    // Type filter
    if (filters.documentType !== "all") {
      docs = docs.filter((d) => d.documentType === filters.documentType);
    }

    // Counterparty filter
    if (filters.counterparty) {
      const query = filters.counterparty.toLowerCase();
      docs = docs.filter((d) => d.counterpartyName.toLowerCase().includes(query));
    }

    // Date range filter
    if (filters.dateFrom) {
      docs = docs.filter((d) => d.documentDate >= filters.dateFrom);
    }
    if (filters.dateTo) {
      docs = docs.filter((d) => d.documentDate <= filters.dateTo);
    }

    // Sort
    docs.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortOrder === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    return docs;
  }, [documents, selectedClientId, selectedFolder, filters, sortField, sortOrder]);

  // Paginate
  const paginatedDocuments = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredDocuments.slice(start, start + pageSize);
  }, [filteredDocuments, page]);

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortOrder("asc");
      }
      setPage(1);
    },
    [sortField]
  );

  const handleUpload = useCallback(
    (files: File[], clientId: string, mirrorClientId: string | null) => {
      const newDocs: MockDocument[] = files.map((file, i) => ({
        id: `upload-${Date.now()}-${i}`,
        originalFilename: file.name,
        storedFilename: "",
        documentType: "other",
        documentDate: new Date().toISOString().split("T")[0],
        uploadDate: new Date().toISOString().split("T")[0],
        counterpartyName: "",
        counterpartyEik: "",
        amount: 0,
        currency: "BGN",
        status: "processing" as const,
        clientId,
        folder: "other",
        aiConfidence: 0,
        description: "",
      }));

      setDocuments((prev) => [...newDocs, ...prev]);
      showSuccessToast(`${files.length} file(s) uploading...`);

      // Simulate processing
      setTimeout(() => {
        setDocuments((prev) =>
          prev.map((d) =>
            newDocs.some((nd) => nd.id === d.id)
              ? {
                  ...d,
                  status: "processed" as const,
                  storedFilename: `2025-03-27-${d.originalFilename}`,
                  documentType: "invoice_inbound",
                  folder: "invoices/inbound",
                  aiConfidence: 0.92,
                  counterpartyName: "Auto-detected Supplier",
                  description: "AI processed document",
                }
              : d
          )
        );
        showSuccessToast(`${files.length} file(s) processed`);

        // Create mirror copies if requested
        if (mirrorClientId && mirrorClientId !== "none") {
          const mirrorDocs: MockDocument[] = newDocs.map((d) => ({
            ...d,
            id: `mirror-${d.id}`,
            clientId: mirrorClientId,
            status: "processed" as const,
            documentType: "invoice_outbound",
            folder: "invoices/outbound",
            storedFilename: `2025-03-27-${d.originalFilename}`,
            aiConfidence: 0.92,
          }));
          setDocuments((prev) => [...mirrorDocs, ...prev]);
        }
      }, 2000);
    },
    []
  );

  const handleDelete = useCallback(() => {
    if (!deleteDocId) return;
    setDocuments((prev) => prev.filter((d) => d.id !== deleteDocId));
    if (previewDocId === deleteDocId) setPreviewDocId(null);
    setDeleteDocId(null);
    showSuccessToast("Document deleted");
  }, [deleteDocId, previewDocId]);

  const handleMirror = useCallback(() => {
    if (!mirrorDocId || !mirrorTargetClient) return;
    const doc = documents.find((d) => d.id === mirrorDocId);
    if (!doc) return;

    const flippedType =
      doc.documentType === "invoice_inbound"
        ? "invoice_outbound"
        : doc.documentType === "invoice_outbound"
          ? "invoice_inbound"
          : doc.documentType;

    const mirrorDoc: MockDocument = {
      ...doc,
      id: `mirror-${Date.now()}`,
      clientId: mirrorTargetClient,
      documentType: flippedType,
      folder: flippedType === "invoice_outbound" ? "invoices/outbound" : doc.folder,
    };

    setDocuments((prev) => [mirrorDoc, ...prev]);
    setMirrorDocId(null);
    setMirrorTargetClient("");
    const targetClient = mockClients.find((c) => c.id === mirrorTargetClient);
    showSuccessToast(`Document mirrored to ${targetClient?.name}`);
  }, [mirrorDocId, mirrorTargetClient, documents]);

  const previewDocument = documents.find((d) => d.id === previewDocId) || null;
  const mirrorDoc = documents.find((d) => d.id === mirrorDocId);

  return (
    <div className="flex h-[calc(100vh-100px)]">
      {/* Left sidebar */}
      <div className="w-60 shrink-0 border-r pr-4 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-neutral-500 mb-1.5 block">Client</label>
            <Select value={selectedClientId} onValueChange={(v) => { setSelectedClientId(v); setPage(1); }}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {mockClients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 mb-1.5 block">Folders</label>
            <FolderTree
              folders={folderTree}
              selectedPath={selectedFolder}
              onSelect={(path) => { setSelectedFolder(path); setPage(1); }}
            />
          </div>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 pl-4 flex flex-col min-w-0 overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-semibold">File Explorer</h1>
          <Button size="sm" onClick={() => setUploadDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-1.5" />
            Upload
          </Button>
        </div>

        <div className="mb-4">
          <FilterBar
            filters={filters}
            onFiltersChange={(f) => { setFilters(f); setPage(1); }}
          />
        </div>

        <div className="flex-1 min-h-0 flex overflow-hidden">
          <div className={`flex-1 min-w-0 overflow-y-auto ${previewDocId ? "pr-4" : ""}`}>
            <FileTable
              documents={paginatedDocuments}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              onPreview={setPreviewDocId}
              onDelete={setDeleteDocId}
              onMirror={setMirrorDocId}
              page={page}
              pageSize={pageSize}
              total={filteredDocuments.length}
              onPageChange={setPage}
            />
          </div>

          {previewDocId && (
            <DocumentPreviewPanel
              document={previewDocument}
              onClose={() => setPreviewDocId(null)}
            />
          )}
        </div>
      </div>

      {/* Upload dialog */}
      <UploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUpload={handleUpload}
        preselectedClientId={selectedClientId !== "all" ? selectedClientId : undefined}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteDocId} onOpenChange={(open) => !open && setDeleteDocId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDocId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mirror dialog */}
      <Dialog open={!!mirrorDocId} onOpenChange={(open) => { if (!open) { setMirrorDocId(null); setMirrorTargetClient(""); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Mirror Document</DialogTitle>
            <DialogDescription>
              {mirrorDoc && `Mirror "${mirrorDoc.storedFilename || mirrorDoc.originalFilename}" to another client.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium mb-1.5 block">Target Client</label>
            <Select value={mirrorTargetClient} onValueChange={setMirrorTargetClient}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {mockClients
                  .filter((c) => c.id !== mirrorDoc?.clientId)
                  .map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setMirrorDocId(null); setMirrorTargetClient(""); }}>Cancel</Button>
            <Button onClick={handleMirror} disabled={!mirrorTargetClient}>Mirror</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
